import { useQueryClient } from "@tanstack/react-query";
import { usePeriodReview } from "@/profitability/overview/period-review";
import {
  latestCachedOverview,
  useReportOverview,
} from "@/profitability/overview/query";
import {
  openFindingCount,
  useFindings,
  useFindingsReview,
} from "@/profitability/review/query";
import { formatReportPeriod, runDates } from "@/profitability/utils";

/**
 * The Review reads the same queries, so the header and the Review share one
 * request each. The latest period counts everything still open; an earlier
 * one, what it flagged.
 */
export const useTemplatePeriod = (
  businessId: string,
  reportId: string,
  periodId: string | null,
  compare: string | null,
) => {
  const client = useQueryClient();
  // The previous period stays up while the next one loads, so the header
  // and the column do not empty out. `periodChanging` is that stand-in
  // belonging to a different week: its numbers are not this week's.
  const overview = useReportOverview(
    businessId,
    reportId,
    periodId,
    compare,
    true,
    () => latestCachedOverview(client, businessId, reportId),
  );
  const run = overview.data?.run ?? null;
  const requestedId = periodId ?? overview.data?.latest_run_id ?? null;
  const periodChanging =
    overview.isPlaceholderData &&
    !!run &&
    !!requestedId &&
    run.id !== requestedId;
  const findingsReview = useFindingsReview(
    businessId,
    periodChanging ? null : run,
  );
  const review = usePeriodReview(
    businessId,
    periodChanging ? "" : (run?.id ?? ""),
  );
  const findings = useFindings(
    businessId,
    reportId,
    "open",
    periodId ? { runId: periodId } : undefined,
  );
  const listed = overview.data?.periods?.find(
    (period) => period.run_id === requestedId,
  );
  const activeCount = findings.isSuccess
    ? openFindingCount(findings.data?.pages[0]?.counts)
    : (listed?.active_findings ??
      openFindingCount(findings.data?.pages[0]?.counts));
  const dates = run ? runDates(run) : null;
  let label: string | null = null;
  if (periodChanging) label = listed ? formatReportPeriod(listed) : null;
  else if (dates) label = formatReportPeriod(dates);
  else if (run) label = "Latest report";
  return {
    overview,
    run,
    review,
    findings,
    findingsReview,
    activeCount,
    /** The week the URL asked for, once the period list can name it. */
    requestedId,
    /** The stand-in on screen is a different week from the one asked for. */
    periodChanging,
    /** "Oct 12–18, 2026"; null before the period loads or without one. */
    label,
  };
};

export type TemplatePeriod = ReturnType<typeof useTemplatePeriod>;
